import {TimeslotProps} from 'src/types/types'

// Same regex as src/pages-new/passwordRecovery/PasswordRecoveryPageNew.tsx — reused for
// consistency across the new-style pre-auth pages (the legacy FirstStep.tsx used a much
// heavier RFC-ish regex; this one is the established new-style convention instead).
export const EMAIL_REGEX = /^\w+([+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/

// Identical to src/pages/selfRegistration/steps/SecondStep/SecondStep.tsx:18 — business
// rule, kept exactly (min 8 chars, at least one lowercase/uppercase/digit/special char).
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()]).{8,}$/

// Identical to SecondStep.tsx:17 — digits only (or empty, required-ness is checked
// separately at the field level, same split as legacy).
export const POSTAL_CODE_REGEX = /^[0-9]*$/

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

export function isValidPassword(password: string): boolean {
  return PASSWORD_REGEX.test(password)
}

export function isValidPostalCode(postalCode: string): boolean {
  return POSTAL_CODE_REGEX.test(postalCode)
}

export interface AddressValue {
  country: string
  region: string
  province: string
  city: string
  address: string
  streetNumber: string
  postalCode: string
  locality: string
}

export interface ConsentOption {
  value: string
  label: string
  required: boolean
}

export interface PersonalData {
  firstName: string
  lastName: string
  password: string
  fiscalCode: string
  cellPhoneNumber: string
  email: string
}

export interface BuildRegistrationBodyParams {
  personalData: PersonalData
  address: AddressValue
  timeSlots: TimeslotProps[]
  consentOptions: ConsentOption[]
  consents: Record<string, string>
  activationCodeRequired: boolean
  code: string
}

/**
 * New-style equivalent of the legacy `onFinish` in
 * src/pages/selfRegistration/steps/SecondStep/SecondStep.tsx:99-123. Same submitted
 * shape, with two differences: `confirmPassword`/`consentPassword` never entered the body
 * to begin with (kept as separate local-only fields here, so there's nothing to delete),
 * and `birthDate` is dropped entirely — the legacy form never actually collects it (no
 * matching Form.Item exists), so `body.birthDate` was always `undefined` there.
 */
export function buildRegistrationBody({
  personalData,
  address,
  timeSlots,
  consentOptions,
  consents,
  activationCodeRequired,
  code,
}: BuildRegistrationBodyParams): Record<string, any> {
  const privacyConsents = consentOptions
    .filter(opt => consents[opt.value] === 'CONSENT')
    .map(opt => opt.value)

  const body: Record<string, any> = {
    firstName: personalData.firstName,
    lastName: personalData.lastName,
    password: personalData.password,
    fiscalCode: personalData.fiscalCode,
    cellPhoneNumber: personalData.cellPhoneNumber,
    email: personalData.email,
    country: address.country,
    region: address.region,
    province: address.province,
    city: address.city,
    address: address.address,
    streetNumber: address.streetNumber,
    postalCode: address.postalCode,
    locality: address.locality,
    privacyConsents,
    preferredTimeSlots: timeSlots,
  }

  if (activationCodeRequired && code) {
    body.token = code
  }

  return body
}
